import { ActionTree } from "vuex";
import { RootState } from "@/store/interfaces/root";
import { UserState, UserActions } from "@/store/interfaces/user";
import { ActionTypes } from "./action-types";
import { MutationTypes } from "./mutation-types";
import { Fetch } from "@/plugins/fetch";
import moment from "moment";

export const actions: ActionTree<UserState, RootState> & UserActions = {
  /**
    Fetch user session status
    @var commit - start mutation handler
    @param payload - x-csrf token: string
  */
  async [ActionTypes.FETCH_USER_STATUS]({ commit }, payload) {
    await Fetch.post("user/getuserstatus", payload)
      .then(res => {
        commit(MutationTypes.SET_USER_STATUS, {
          status: res.status,
          didInvalidate: false,
          receivedAt: moment().unix(),
          connectionError: false,
          user: res.user
        });
      })
      .catch(error => {
        commit(MutationTypes.SET_USER_STATUS, {
          status: false,
          didInvalidate: true,
          receivedAt: moment().unix(),
          connectionError: true,
          user: {}
        });
        throw error;
      });
  },

  /**
    Login with provided user
    @var commit - start mutation handler
    @param payload - {
      credentials: {
          email: string;
          password: string;
      };
      x-csrf token: string
    }
  */
  async [ActionTypes.USER_LOGIN]({ commit }, payload) {
    await Fetch.post("user/login", payload.token, payload.credentials)
      .then(res => {
        commit(MutationTypes.SET_USER_STATUS, {
          status: res.status,
          didInvalidate: false,
          receivedAt: moment().unix(),
          connectionError: false,
          user: res
        });
      })
      .catch(error => {
        if (error.code === 400) {
          commit(MutationTypes.SET_USER_STATUS, {
            status: false,
            didInvalidate: true,
            receivedAt: moment().unix(),
            connectionError: false,
            user: {}
          });
          throw error;
        } else {
          commit(MutationTypes.SET_USER_STATUS, {
            status: false,
            didInvalidate: false,
            receivedAt: moment().unix(),
            connectionError: true,
            user: {}
          });
          throw error;
        }
      });
  },

  /**
    Logout with current session user
    @var commit - start mutation handler
    @param payload - x-csrf token: string
  */
  async [ActionTypes.USER_LOGOUT]({ commit }, payload) {
    await Fetch.post("user/logout", payload)
      .then(() => {
        commit(MutationTypes.SET_USER_STATUS, {
          status: false,
          didInvalidate: false,
          receivedAt: moment().unix(),
          connectionError: false,
          user: {}
        });
      })
      .catch(error => {
        throw error;
      });
  },

  /**
    Request password reset
    @var commit - start mutation handler
    @param payload - 
      data: {
        email?: string | undefined
        name?: string | undefined
      },
      x-csrf token: string
  */
  async [ActionTypes.USER_REQUEST_PASSWORD_RESET]({ commit }, payload) {
    await Fetch.post("user/resetpassword", payload.token, payload.data)
      .then(res => {
        commit(MutationTypes.SET_REQUEST_PASSWORD_RESET_STATUS, {
          status: res,
          didInvalidate: false,
          receivedAt: moment().unix(),
          connectionError: false
        });
      })
      .catch(error => {
        commit(MutationTypes.SET_REQUEST_PASSWORD_RESET_STATUS, {
          status: false,
          didInvalidate: true,
          receivedAt: moment().unix(),
          connectionError: true
        });
        throw error;
      });
  },

  /**
    Login after changing password
    @var commit - start mutation handler
    @param payload - 
      credentials: {
        uid: string;
        expire: string;
        hash: string;
        token: string;
      },
      x-csrf token: string
  */
  async [ActionTypes.USER_ONETIME_LOGIN]({ commit }, payload) {
    await Fetch.post("user/onetimelogin", payload.token, payload.credentials)
      .then(res => {
        commit(MutationTypes.SET_USER_STATUS, {
          status: res.status,
          didInvalidate: false,
          receivedAt: moment().unix(),
          connectionError: false,
          user: res
        });
      })
      .catch(error => {
        commit(MutationTypes.SET_USER_STATUS, {
          status: false,
          didInvalidate: true,
          receivedAt: moment().unix(),
          connectionError: false,
          user: {}
        });
        throw error;
      });
  },

  /**
    Validate signup data
    @var commit - start mutation handler
    @param payload - 
      data: {
        company: string;
        email: string;
        first_name: string;
        language: string;
        last_name: string;
        office: string;
        password: string;
        phone: string;
        timezone: string;
        vello_url: string;
        voucher: string;
      };
      x-csrf token: string
  */
  async [ActionTypes.USER_VALIDATE_SIGNUP]({ commit }, payload) {
    await Fetch.post("user/validatesignup", payload.token, payload.data)
      .then(res => {
        res.email_already_exists
          ? commit(MutationTypes.SET_REGISTER_VALIDATION_STATUS, {
              status: false,
              didInvalidate: true,
              receivedAt: moment().unix(),
              connectionError: false,
              response: res
            })
          : commit(MutationTypes.SET_REGISTER_VALIDATION_STATUS, {
              status: true,
              didInvalidate: false,
              receivedAt: moment().unix(),
              connectionError: false,
              response: res
            });
      })
      .catch(error => {
        commit(MutationTypes.SET_REGISTER_VALIDATION_STATUS, {
          status: false,
          didInvalidate: true,
          receivedAt: moment().unix(),
          connectionError: true,
          response: error
        });
        throw error;
      });
  },

  /**
    Register with provided details
    @var commit - start mutation handler
    @param payload - 
      data: {
        first_name: string;
        last_name: string;
        phone: string;
        name: string;
        email: string;
        password: string;
        language: string;
        timezone: string;
      };
      x-csrf token: string
  */
  async [ActionTypes.USER_REGISTER]({ commit }, payload) {
    await Fetch.post("user", payload.token, payload.data)
      .then(res => {
        commit(MutationTypes.SET_REGISTER_STATUS, {
          status: res.status,
          didInvalidate: false,
          receivedAt: moment().unix(),
          flowState: "base",
          email: res.email,
          error: false
        });
      })
      .catch(error => {
        commit(MutationTypes.SET_REGISTER_STATUS, {
          status: false,
          didInvalidate: true,
          receivedAt: moment().unix(),
          flowState: "base",
          email: "",
          error: true
        });
        throw error;
      });
  }
};
